import React, { useEffect } from 'react'
import { Layout } from '../../common/layout'
import { useIntl } from 'react-intl'
import { colors } from 'app-v2/design-systems/theme/colors'
import { CodeCheck } from './components/code-check/CodeCheck'
import { H2, Text } from 'app/common/typography'
import { fonts } from 'app-v2/design-systems/typography/fonts'
import { DuelsCarousel } from './components/duels-carousel/DuelsCarousel'
import { breakpoints, media, ResponsiveContainer } from '../../assets/styles'
import { Spacer } from 'app-v2/design-systems/components/Spacer/Spacer'
import { BORDER, SIZE, SPACING } from 'app-v2/design-tokens'
import { QuoteCarousel } from './components/quotes-carousel/QuoteCarousel'
import { FAQ } from './components/faq/FAQ'
import { Legals } from './components/legals/Legals'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import type { TPKState } from 'shared/store/tu-preferes-king'
import { fetchTPKPageData } from 'shared/store/tu-preferes-king'
import { Nl2Br } from './components/nl2br/Nl2Br'
import { FlameIcon } from './assets/FlameIcon'
import { ExplanationsToPlay } from './components/explanations/ExplanationsToPlay'
import { ExplanationsToClaim } from './components/explanations-to-claim/ExplanationsToClaim'
import { ExplanationsToBigWin } from './components/explanations/ExplanationsToBigWin'
import { LoadingCodeCheck } from './components/code-check/LoadingCodeCheck'
import { useWindowDimensions } from '../utils'
import { TPKColors } from '../colors'

import styled from 'styled-components'
//$FlowFixMe
import tpkLogo from 'app/tu-preferes-king/assets/tpk-2024-logo.webp'
//$FlowFixMe
import header from 'app/tu-preferes-king/assets/tpk-2024-wave-header.webp'
//$FlowFixMe
import surf from 'app/tu-preferes-king/assets/tpk-2024-surf.webp'
//$FlowFixMe
import buoy from 'app/tu-preferes-king/assets/tpk-2024-buoy.webp'

export function TuPreferesKingHome() {
    const intl = useIntl()
    const { width: windowWidth } = useWindowDimensions()
    const isSmall = windowWidth <= breakpoints.small
    const state: TPKState = useSelector((s: RootState) => s.tuPreferesKingState)
    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(fetchTPKPageData())
    }, [dispatch])

    const started = !!state.pageData && state.pageData.startDate <= Date.now()
    const ended = !!state.pageData && state.pageData.endDate <= Date.now()
    const disabled = !started || ended
    return (
        <Layout color="transparent">
            <StyledPageContainer>
                <StyledBackground />

                <StyledSurfImage src={surf} width={isSmall ? 100 : 200} />
                <StyledBuoyImage src={buoy} width={isSmall ? 100 : 200} />

                <StyledCodeLayoutContainer>
                    <StyledCodeLayout>
                        <img
                            src={tpkLogo}
                            width={isSmall ? 200 : 380}
                            alt={'tpkLogo'}
                        />

                        <StyledHeaderTextContainer>
                            <H2
                                center
                                font={fonts.flameRegular}
                                color={colors.white}>
                                <Nl2Br>
                                    {intl.formatMessage({
                                        id:
                                            'tupreferesking.home.code-input-heading'
                                    })}
                                </Nl2Br>
                            </H2>
                        </StyledHeaderTextContainer>

                        <Spacer size={SIZE.XS} />

                        {state.pageData ? (
                            <CodeCheck
                                disable={disabled}
                                captchaMode={state.pageData.recaptcha}
                            />
                        ) : (
                            <LoadingCodeCheck />
                        )}
                    </StyledCodeLayout>
                </StyledCodeLayoutContainer>

                <StyledContentContainer>
                    <StyledTitleContainer>
                        {!isSmall && <StyledLine />}
                        <H2 font={fonts.flameBold}>
                            {intl.formatMessage({
                                id: 'tupreferesking.home.promotionCaption1'
                            })}
                        </H2>
                        <FlameIcon size={SPACING.XL} />
                        {!isSmall && <StyledLine />}
                    </StyledTitleContainer>

                    <StyledPromoCaption>
                        <Text
                            font={fonts.flameSansRegular}
                            color={colors.bbqBrown}>
                            <Nl2Br>
                                {intl.formatMessage({
                                    id: 'tupreferesking.home.promotionCaption2'
                                })}
                            </Nl2Br>
                        </Text>
                        <Text font={fonts.flameRegular} color={colors.bbqBrown}>
                            <Nl2Br>
                                {intl.formatMessage({
                                    id: 'tupreferesking.home.promotionCaption3'
                                })}
                            </Nl2Br>
                        </Text>
                    </StyledPromoCaption>
                </StyledContentContainer>

                <DuelsCarousel />

                <ResponsiveContainer>
                    <ExplanationsToPlay />
                </ResponsiveContainer>

                <QuoteCarousel />

                <ResponsiveContainer>
                    <Spacer size={SIZE.M} />
                    <ExplanationsToClaim />
                </ResponsiveContainer>

                <ResponsiveContainer>
                    <ExplanationsToBigWin />
                </ResponsiveContainer>

                <FAQ />

                <ResponsiveContainer>
                    <Legals />
                </ResponsiveContainer>
                <Spacer size={SIZE.XXS} />
            </StyledPageContainer>
        </Layout>
    )
}

const StyledBuoyImage = styled.img`
    position: absolute;
    top: 200px;
    right: 7%;

    @media (max-width: 1400px) {
        top: 100px;
        right: -100px;
    }

    @media (max-width: ${breakpoints.small}px) {
        top: 100px;
        right: -50px;
    }
`

const StyledSurfImage = styled.img`
    position: absolute;
    top: 50px;
    left: 5%;

    @media (max-width: 1050px) {
        top: 0px;
        left: -50px;
    }

    @media (max-width: ${breakpoints.small}px) {
        top: 10px;
        left: -50px;
    }
`

const StyledCodeLayout = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 24px;
    width: calc(100% - 24px);
    max-width: 886px;
    position: relative;
`

const StyledCodeLayoutContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    margin-top: 120px;

    @media (max-width: ${breakpoints.medium}px) {
        margin-top: 90px;
    }
`

const StyledBackground = styled.div`
    position: absolute;
    width: 100%;
    height: 520px;
    background-image: url(${header});
    background-position: bottom;
    background-repeat: no-repeat;
    background-size: cover;

    @media (max-width: ${breakpoints.medium}px) {
        max-height: 480px;
    }

    @media (max-width: ${breakpoints.small}px) {
        height: 350px;
    }
`

const StyledContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: auto;
    background-color: ${colors.mayoEggWhite};
    padding: 0 ${SPACING.L};
    justify-content: center;
    ${media.small`align-items: center;`}
    margin: 0 auto;
`

const StyledPageContainer = styled.div`
    margin: 0 auto;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${colors.mayoEggWhite};
    margin-top: ${props => (props.isForGoodies ? '-20px' : null)};
    gap: ${SPACING.XXL};
`

const StyledTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${SPACING.M};
`

const StyledPromoCaption = styled.p`
    ${media.small`text-align: center;`}
    display: flex;
    flex-direction: column;
    gap: ${SPACING.XXS};
    max-width: 900px;
`

const StyledLine = styled.div`
    display: flex;
    width: 130px;
    border-top: ${BORDER.WIDTH.XS} solid ${TPKColors.circleColor};
`

const StyledHeaderTextContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 0 ${SPACING.M} ${SPACING.M};
    ${media.medium`padding: ${SPACING.M};`}
`
